import {
  Classes,
  Intent,
  PopoverInteractionKind,
  Position,
} from "@blueprintjs/core";
import {
  AgentChatThreadId,
  CollectionId,
  HexId,
  StarredType,
  hexIdToHumanHexId,
} from "@hex/common";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RuntypeBrand } from "runtypes";

import { HexButton, HexTooltip } from "../../hex-components";
import { ORG_ID } from "../../orgs";
import { Routes } from "../../route/routes";
import { ClientCounterEvent } from "../../util/clientCounterEvent";
import { trackCounterEvent } from "../../util/trackEvent";
import { ProjectCardPopover } from "../common/project-cards/ProjectCardPopover";
import { useToaster } from "../common/Toasts";
import { FixedHeightMenuItem } from "../global-sidebar/favorite-projects-and-collection-rows/styled";
import { isModifiedEvent } from "../global-sidebar/favorite-projects-and-collection-rows/utils";
import { StarredCollectionForSidebarFragment } from "../global-sidebar/FavoriteCollections.generated";
import { useStarHex } from "../hex-list-2/hex-row/hooks/useStarHex";
import {
  CollectionIcon,
  ComponentIcon,
  EditIcon,
  MoreMenuIcon,
  ProjectIcon,
  PublishedProjectIcon,
  ThreadIcon,
} from "../icons/CustomIcons";

import { useStarCollection } from "./collections-tab/hooks/useStarCollection";
import { HomeSidebarProjectMoreOptions } from "./sidebar/HomeSideBarProjectMoreOptions";
import { MenuItemTextWithShowOnHover } from "./sidebar/MenuItemTextWithShowOnHover";

export interface SmallRowNavItemProps {
  isPublished: boolean;
  id:
    | (string & RuntypeBrand<"HexId">)
    | (string & RuntypeBrand<"CollectionId">)
    | (string & RuntypeBrand<"AgentChatThreadId">);
  canEdit: boolean;
  type: StarredType;
  canViewLogic: boolean;
  title: string;
  referrerParams?: Record<string, string>;
  isStarred: boolean;
  emoji?: StarredCollectionForSidebarFragment["emoji"];
  counterEventName?: ClientCounterEvent;
}

export const SmallRowNavItem: React.ComponentType<SmallRowNavItemProps> =
  React.memo(function SmallRowNavItem({
    canEdit,
    canViewLogic,
    counterEventName,
    emoji,
    id,
    isPublished,
    isStarred,
    referrerParams,
    title,
    type,
  }) {
    const navigate = useNavigate();
    const toaster = useToaster();
    const isComponent = type === StarredType.COMPONENT;
    const isCollection = type === StarredType.COLLECTION;
    const isThread = type === StarredType.ASK;

    const handleStarHexOnError = useCallback(
      (isHexStarred: boolean) => {
        toaster.show({
          message: isHexStarred
            ? "There was an error removing this project from your favorites."
            : "There was an error adding this project to your favorites.",
          intent: Intent.DANGER,
        });
      },
      [toaster],
    );

    const handleStarHexChange = useStarHex(
      HexId.check(id),
      handleStarHexOnError,
    );
    const handleStarCollectionChange = useStarCollection(
      CollectionId.check(id),
      handleStarHexOnError,
    );
    const handleRemoveFavorite = useCallback(() => {
      if (isCollection && CollectionId.guard(id)) {
        handleStarCollectionChange(false);
      } else if (HexId.guard(id)) {
        handleStarHexChange(false);
      } else {
        throw new Error(
          `ID ${id} is not of type ${isCollection ? "CollectionId" : "HexId"}`,
        );
      }
    }, [handleStarHexChange, handleStarCollectionChange, isCollection, id]);

    const metadata: {
      url: string;
      icon: React.ReactElement;
      onClick: () => void;
    } = useMemo(() => {
      switch (type) {
        case StarredType.COLLECTION:
          if (!CollectionId.guard(id)) {
            throw new Error(`Expected ID to have type CollectionId: ${id}`);
          }
          return {
            url: Routes.COLLECTION.href(ORG_ID, true, {
              collectionId: id,
              collectionName: title,
            }),
            icon: emoji ? <span>{emoji}</span> : <CollectionIcon />,
            onClick: () => {
              Routes.COLLECTION.push(navigate, {
                collectionId: id,
                collectionName: title,
              });
            },
          };
        case StarredType.COMPONENT: {
          if (!HexId.guard(id)) {
            throw new Error(`Expected ID to have type HexId: ${id}`);
          }
          const hexLocator = hexIdToHumanHexId(id, title);
          return {
            url: Routes.COMPONENT.href(ORG_ID, true, {
              hexLocator,
              version: "draft",
            }),
            icon: <ComponentIcon />,
            onClick: () => {
              Routes.COMPONENT.push(navigate, {
                hexLocator,
                version: "draft",
              });
            },
          };
        }
        case StarredType.ASK: {
          if (!AgentChatThreadId.guard(id)) {
            throw new Error(
              `Expected ID to have type AgentChatThreadId: ${id}`,
            );
          }
          return {
            url: Routes.THREAD.href(ORG_ID, true, { threadId: id }),
            icon: <ThreadIcon />,
            onClick: () => {
              Routes.THREAD.push(navigate, { threadId: id });
            },
          };
        }
        case StarredType.PROJECT:
        default: {
          if (!HexId.guard(id)) {
            throw new Error(`Expected ID to have type HexId: ${id}`);
          }
          const hexLocator = hexIdToHumanHexId(id, title);
          return isPublished
            ? {
                url: Routes.APP_LATEST.href(ORG_ID, true, {
                  hexLocator,
                  queryParams: referrerParams,
                }),
                icon: <PublishedProjectIcon />,
                onClick: () => {
                  Routes.APP_LATEST.push(navigate, {
                    hexLocator,
                    queryParams: referrerParams,
                  });
                },
              }
            : {
                url: Routes.LOGIC.href(ORG_ID, true, {
                  hexLocator,
                  version: "draft",
                }),
                icon: <ProjectIcon />,
                onClick: () => {
                  Routes.LOGIC.push(navigate, {
                    hexLocator,
                    version: "draft",
                  });
                },
              };
        }
      }
    }, [emoji, id, isPublished, referrerParams, title, type, navigate]);

    const onClickDraftLink = useCallback(
      (e: React.MouseEvent) => {
        if (!HexId.guard(id)) {
          throw new Error(`Expected ID to have type HexId: ${id}`);
        }
        const hexLocator = hexIdToHumanHexId(id, title);
        e.stopPropagation();
        Routes.LOGIC.push(navigate, {
          hexLocator,
          version: "draft",
        });
      },
      [navigate, id, title],
    );

    const onClick = useCallback(
      (evt: React.MouseEvent) => {
        const withModifier = isModifiedEvent(evt);
        if (withModifier) {
          window.open(metadata.url, "_blank");
          return;
        }
        metadata.onClick();

        // Track the event if it exists
        if (counterEventName) {
          trackCounterEvent(counterEventName);
        }
      },
      [metadata, counterEventName],
    );

    const projectMoreActions = (
      <HomeSidebarProjectMoreOptions
        canEdit={canEdit}
        canViewLogic={canViewLogic}
        collectionName={isCollection ? title : undefined}
        href={metadata.url}
        id={id}
        isPublished={isPublished}
        isStarred={isStarred}
        title={title}
        type={type}
        onRemoveFavorite={handleRemoveFavorite}
      />
    );

    const inlineGoToDraftAction = (
      <HexTooltip
        content={canEdit ? "Edit draft" : "View notebook"}
        position={Position.BOTTOM_RIGHT}
      >
        <HexButton
          icon={<EditIcon />}
          minimal={true}
          small={true}
          onClick={onClickDraftLink}
        />
      </HexTooltip>
    );

    const hoverTargetId = useMemo(() => `${id}-sidebar-hover-container`, [id]);

    const [ellipsisVisible, setEllipsisVisible] = useState(false);
    const nameRef = useCallback((el: HTMLDivElement) => {
      setEllipsisVisible(el ? el.offsetWidth < el.scrollWidth : false);
    }, []);

    return (
      <HexTooltip
        content={title}
        disabled={!ellipsisVisible}
        hoverOpenDelay={300}
        position="right"
        targetTagName="div"
      >
        <FixedHeightMenuItem
          icon={metadata.icon}
          text={
            <MenuItemTextWithShowOnHover
              hoverContent={
                <>
                  {isPublished &&
                    canViewLogic &&
                    !isComponent &&
                    !isCollection &&
                    !isThread &&
                    inlineGoToDraftAction}
                  <ProjectCardPopover
                    interactionKind={PopoverInteractionKind.CLICK}
                    popoverContent={projectMoreActions}
                  >
                    <HexButton
                      icon={<MoreMenuIcon />}
                      minimal={true}
                      small={true}
                    />
                  </ProjectCardPopover>
                </>
              }
              id={hoverTargetId}
              text={
                <div ref={nameRef} className={Classes.TEXT_OVERFLOW_ELLIPSIS}>
                  {title}
                </div>
              }
            />
          }
          onClick={onClick}
        />
      </HexTooltip>
    );
  });
